/**
 * ════════════════════════════════════════════════════════════════════
 * ★★★ 체험하기(어린이/학생용). 절대 인증 가드를 붙이지 마라 ★★★
 * ════════════════════════════════════════════════════════════════════
 * 이 화면은 회사 정책상 로그인 없이 들어오는 공개 페이지다(체험하기 무인증 원칙).
 * `useAuthGuard` 같은 인증 가드를 이 파일에 추가하면 정책 위반이다.
 * 과거에 인증 가드를 여러 페이지에 한꺼번에 적용하는 작업에 이 페이지가 휩쓸려
 * 들어가 로그인이 강제된 사고가 있었다. 가드를 일괄 적용하는 작업을 할 때는
 * 이 파일(`play.tsx`)과 `play1.tsx`를 반드시 명시적으로 제외해야 한다.
 * BE 쪽도 같은 원칙으로 `/app/play/**`를 인증 없이 통과시킨다(security-ignore-uris).
 *
 * 화면: 초중고 학생 대상 진단 체험. URL `/play`.
 * 흐름: 외부 홍보 링크/QR 등으로 직접 진입 → 학생 정보(지역·학교·학년·닉네임)와
 * 법정대리인 정보(선택) 입력 → 문항 응답(Q1~12 단일 선택, Q13은 6개 역량 순위 매기기)
 * → 제출(POST) → 결과 화면(해봄랜드 유형) + 만족도 설문 → 설문 제출(PATCH) 완료 후
 * "홈으로 이동" 버튼으로 `/`로 나간다.
 * 데이터: `@/fetcher/usePostExhibition`의 `postPlay`/`patchPlaySurvey` 사용
 * (일반 성인 체험용 `postExhibition`/`patchExhibitionSurvey`와는 다른 엔드포인트다,
 * `play1.tsx` 참고). 문항 데이터는 `@/constants/exhibition`에 하드코딩.
 * 이 화면은 로그인 페이지들과 달리 sessionStorage 토큰을 아예 참조하지 않는다.
 */
import React, { useState, useLayoutEffect } from "react";
import Header from "@/components/layout/Header";
import { EXHIBITION_QUESTIONS, CHARACTER_DATA, REPRESENTATIVE_QUESTIONS, COMPETENCY_MAPPING } from "@/constants/exhibition";
import Head from "next/head";
import { postPlay, patchPlaySurvey } from "@/fetcher/usePostExhibition";
import { FCType } from "@/types/exhibition";
import { formatPhoneAsYouType, toPhoneDigits } from "@/utils/formatPhoneAsYouType";

const GRADE_OPTIONS = [
    "초등 1학년", "초등 2학년", "초등 3학년", "초등 4학년", "초등 5학년", "초등 6학년",
    "중등 1학년", "중등 2학년", "중등 3학년",
    "고등 1학년", "고등 2학년", "고등 3학년",
];

/** 학생 정보 입력 → 13문항 응답 → 결과·설문까지 한 컴포넌트가 3단계 화면을 분기 렌더한다. */
const Play = () => {
    // 체험하기, 비로그인 공개 페이지. BE도 /app/play/** 인증 면제(security-ignore-uris).
    const [userInfo, setUserInfo] = useState<{
        school: string;
        grade: string;
        nickname: string;
        region: string;
        gender: string;
        guardianName: string;
        guardianPhone: string;
    }>({
        school: "",
        grade: "",
        nickname: "",
        region: "",
        gender: "",
        guardianName: "",
        guardianPhone: "",
    });
    const [answers, setAnswers] = useState<Record<number, number>>({});
    const [submitted, setSubmitted] = useState(false);
    const [finalSubmitted, setFinalSubmitted] = useState(false);
    const [result, setResult] = useState<any>(null);
    const [submissionId, setSubmissionId] = useState<number | null>(null);
    const [ranking, setRanking] = useState<string[]>([]);

    const [survey, setSurvey] = useState({
        rating: 0,
        feedback: "",
    });
    const [hoverRating, setHoverRating] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    // 제출 시 발견한 첫 빈 필수 항목의 DOM id. 해당 항목에 빨간 강조 ring 표시. 입력하면 해제.
    const [highlightId, setHighlightId] = useState<string | null>(null);

    useLayoutEffect(() => {
        if (submitted && !finalSubmitted) {
            window.scrollTo(0, 0);
        }
    }, [submitted]);

    useLayoutEffect(() => {
        if (finalSubmitted) {
            window.scrollTo(0, 0);
        }
    }, [finalSubmitted]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        let { name, value } = e.target;
        // 전화번호 상태는 숫자만(서버 형식). 화면에서만 하이픈을 붙여 보여 준다.
        if (name === "guardianPhone") {
            value = toPhoneDigits(value);
        }
        setUserInfo((prev) => ({ ...prev, [name]: value }));
        setHighlightId(null);
    };

    const handleOptionChange = (questionId: number, optionId: number) => {
        setHighlightId(null);
        if (questionId === 13) {
            const q13 = EXHIBITION_QUESTIONS[12];
            const selectedOption = q13.options.find(opt => opt.id === optionId);
            const comp = (selectedOption as any)?.competency;

            if (!comp) return;

            setRanking(prev => {
                if (prev.includes(comp)) {
                    if (prev[prev.length - 1] === comp) {
                        return prev.slice(0, -1);
                    }
                    return prev;
                } else {
                    if (prev.length >= 6) return prev;
                    return [...prev, comp];
                }
            });
        } else {
            setAnswers((prev) => ({ ...prev, [questionId]: optionId }));
        }
    };

    const handleResetRanking = () => {
        setRanking([]);
    };

    const isOptionSelected = (questionId: number, optionId: number) => {
        if (questionId === 13) {
            const q13 = EXHIBITION_QUESTIONS[12];
            const selectedOption = q13.options.find(opt => opt.id === optionId);
            const comp = (selectedOption as any)?.competency;
            return ranking.includes(comp);
        }
        return answers[questionId] === optionId;
    };

    const getRankingNumber = (optionId: number) => {
        const q13 = EXHIBITION_QUESTIONS[12];
        const selectedOption = q13.options.find(opt => opt.id === optionId);
        const comp = (selectedOption as any)?.competency;
        const index = ranking.indexOf(comp);
        return index !== -1 ? index + 1 : null;
    };

    const isFormValid = () => {
        const { school, grade, nickname, region, gender, guardianPhone } = userInfo;

        const isSchoolEntered = school.trim().length > 0;
        const isGradeSelected = grade.length > 0;
        const isNicknameEntered = nickname.trim().length > 0;
        const isRegionSelected = region.length > 0;
        const isGenderSelected = gender.length > 0;
        const isGuardianPhoneValid = guardianPhone.length === 0 || /^\d{11}$/.test(guardianPhone);

        const q1To12Answered = EXHIBITION_QUESTIONS.slice(0, 12).every((q) => answers[q.id]);
        const q13Answered = ranking.length === 6;

        return (
            isSchoolEntered &&
            isGradeSelected &&
            isNicknameEntered &&
            isRegionSelected &&
            isGenderSelected &&
            isGuardianPhoneValid &&
            q1To12Answered &&
            q13Answered
        );
    };

    // 강조 ring 클래스. highlightId와 일치하는 항목에만 붙는다.
    const ringCls = (id: string) =>
        highlightId === id ? " ring-2 ring-error-line" : "";

    // 폼 순서(화면 위→아래)대로 첫 빈/유효하지 않은 필수 항목의 DOM id 반환. 없으면 null.
    const getFirstInvalidFieldId = (): string | null => {
        const { school, grade, nickname, region, gender, guardianPhone } = userInfo;
        if (!region) return "field-region";
        if (!gender) return "field-gender";
        if (!school.trim()) return "field-school";
        if (!grade) return "field-grade";
        if (!nickname.trim()) return "field-nickname";
        if (guardianPhone.length > 0 && !/^\d{11}$/.test(guardianPhone))
            return "field-guardianPhone";
        const firstUnanswered = EXHIBITION_QUESTIONS.slice(0, 12).find(
            (q) => !answers[q.id]
        );
        if (firstUnanswered) return `field-q-${firstUnanswered.id}`;
        if (ranking.length !== 6) return "field-q-13";
        return null;
    };

    // 해당 id 항목으로 부드럽게 스크롤(화면 중앙) + 입력 요소면 포커스.
    const scrollToField = (id: string) => {
        const el = document.getElementById(id);
        if (!el) return;
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        if (el instanceof HTMLInputElement || el instanceof HTMLSelectElement) {
            el.focus({ preventScroll: true });
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (isSubmitting) return;
        // 빈 필수 항목이 있으면 제출 대신 그 위치로 이동·강조하고 멈춘다.
        const firstInvalid = getFirstInvalidFieldId();
        if (firstInvalid) {
            setHighlightId(firstInvalid);
            scrollToField(firstInvalid);
            return;
        }
        setIsSubmitting(true);

        // 1. 역량별 점수 합산 (Q1~Q12)
        const counts: Record<string, number> = { W: 0, C: 0, S: 0, M: 0, E: 0, T: 0 };
        const selectedOptionIds: number[] = [];

        EXHIBITION_QUESTIONS.slice(0, 12).forEach(q => {
            const selectedId = answers[q.id];
            selectedOptionIds.push(selectedId);
            const selectedOption = q.options.find(opt => opt.id === selectedId);
            if ((selectedOption as any)?.competency) {
                counts[(selectedOption as any).competency]++;
            }
        });

        // 2. 최대 점수 확인
        const maxScore = Math.max(...Object.values(counts));
        const winners = Object.keys(counts).filter(comp => counts[comp] === maxScore);

        let finalComp = winners[0];

        // 3. 동점자 발생 시 처리
        if (winners.length > 1) {
            // 동점자 처리 1: 대표 문항 정답 개수 비교
            const representativeScores = winners.map(comp => {
                const repQIds = REPRESENTATIVE_QUESTIONS[comp] || [];
                const score = repQIds.reduce((acc, qId) => {
                    const q = EXHIBITION_QUESTIONS.find(item => item.id === qId);
                    const selectedId = answers[qId];
                    const selectedOption = q?.options.find(opt => opt.id === selectedId);
                    return (selectedOption as any)?.competency === comp ? acc + 1 : acc;
                }, 0);
                return { comp, score };
            });

            const maxRepScore = Math.max(...representativeScores.map(s => s.score));
            const repWinners = representativeScores.filter(s => s.score === maxRepScore);

            if (repWinners.length === 1) {
                finalComp = repWinners[0].comp;
            } else {
                // 동점자 처리 2: Q13 순위 비교
                const winnerComps = repWinners.map(w => w.comp);
                const bestRankedComp = winnerComps.reduce((prev, curr) => {
                    const prevRank = ranking.indexOf(prev);
                    const currRank = ranking.indexOf(curr);
                    return currRank < prevRank ? curr : prev;
                });
                finalComp = bestRankedComp;
            }
        }

        const fcTypeKey = COMPETENCY_MAPPING[finalComp] as FCType;
        const topCompetency = CHARACTER_DATA[fcTypeKey];

        const finalResult = {
            userName: userInfo.nickname,
            date: new Date().toLocaleDateString(),
            competency: topCompetency
        };

        try {
            const initialSubmissionData = {
                school: userInfo.school.trim(),
                grade: userInfo.grade,
                nickname: userInfo.nickname.trim(),
                region: userInfo.region,
                gender: userInfo.gender,
                guardianName: userInfo.guardianName.trim(),
                guardianPhone: userInfo.guardianPhone,
                fcType: fcTypeKey,
                answers: [...selectedOptionIds, ...ranking.map(c => (EXHIBITION_QUESTIONS[12].options as any[]).find(o => o.competency === c)?.id || 0)],
                ranking: ranking,
                survey1: 0,
                survey2: "미제출"
            };

            const response = await postPlay(initialSubmissionData);
            if (response && response.result) {
                setSubmissionId(response.result);
            }

            setResult(finalResult);
            setSubmitted(true);
        } catch (error) {
            console.error("Play Initial Post Error:", error);
            alert("제출 중 오류가 발생했습니다. 다시 시도해주세요.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleFinalSubmit = async () => {
        if (isSubmitting || !submissionId) {
            if (!submissionId) alert("제출 번호를 찾을 수 없습니다. 다시 시도해주세요.");
            return;
        }
        setIsSubmitting(true);

        try {
            const surveyData = {
                survey1: survey.rating,
                survey2: survey.feedback.trim()
            };

            await patchPlaySurvey(submissionId, surveyData);
            setFinalSubmitted(true);
        } catch (error) {
            console.error("Play PATCH Error:", error);
            alert("설문 제출 중 오류가 발생했습니다. 다시 시도해주세요.");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (finalSubmitted) {
        return (
            <div key="final-success" className="bg-page min-h-screen">
                <Header />
                <main className="max-w-[1000px] mx-auto py-20 px-6">
                    <div className="bg-white rounded-[20px] p-10 shadow-sm text-center">
                        <img src="/images/exhibition/ppiyak.png" alt="Success" className="w-[300px] mx-auto mb-8" />
                        <h1 className="text-32 font-bold mb-4 font-heading">고생하셨습니다!</h1>
                        <p className="text-18 text-muted mb-12">체험과 설문에 응해주셔서 대단히 감사합니다.</p>

                        <button
                            onClick={() => window.location.href = "/"}
                            className="w-full bg-primary text-white py-4 rounded-[12px] text-18 font-bold hover:bg-primary-hover transition-colors font-heading"
                        >
                            홈으로 이동
                        </button>
                    </div>
                </main>
            </div>
        );
    }

    if (submitted && result) {
        return (
            <div className="bg-page min-h-screen">
                <Header />
                <main className="max-w-[1000px] mx-auto py-20 px-6">
                    <div className="bg-white rounded-[20px] overflow-hidden shadow-sm">
                        <img src="/images/exhibition/dqland_1.jpeg" alt="해봄랜드" className="w-full h-auto" />

                        <div className="p-10">
                            <h2 className="text-24 font-bold text-center mb-8 font-heading">
                                <span className="text-primary">{result.userName}</span>님의 길잡이별 검사 체험 결과
                            </h2>

                            <div className="mb-12">
                                <p className="text-16 text-muted text-center mb-10">진단일시: {result.date}</p>

                                <div className="border-b border-line pb-10 mb-12">
                                    <h3 className="text-22 font-bold mb-6 text-center font-heading">해봄랜드 유형</h3>
                                    <div className="-mx-10 flex justify-center">
                                        <img src={result.competency.image} alt={result.competency.name} className="w-full h-auto" />
                                    </div>
                                </div>

                                <div className="space-y-10">
                                    <div className="text-center">
                                        <h3 className="text-22 font-bold mb-2 font-heading">참여해주셔서 감사합니다! 해봄 성장을 위해 아래 설문에 답변 부탁드립니다 :) </h3>
                                    </div>

                                    <div className="space-y-8 bg-primary-soft rounded-[20px] p-8">
                                        <div className="space-y-4">
                                            <label className="text-18 font-bold text-strong block font-heading">
                                                1. 참여하신 길잡이별 검사 체험은 어땠나요?
                                            </label>
                                            <div className="flex gap-4 justify-center" onMouseLeave={() => setHoverRating(0)}>
                                                {[1, 2, 3, 4, 5].map((star) => (
                                                    <button
                                                        key={star}
                                                        onClick={() => setSurvey(prev => ({ ...prev, rating: star }))}
                                                        onMouseEnter={() => setHoverRating(star)}
                                                        className={`font-heading text-24 font-bold transition-all transform hover:scale-110 ${(hoverRating || survey.rating) >= star ? 'text-warning' : 'text-gray-300'
                                                            }`}
                                                    >
                                                        ★
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="space-y-4">
                                            <label className="text-18 font-bold text-strong block font-heading">
                                                2. 위와 같이 점수를 준 이유나, 검사를 하면서 느낀 점을 자유롭게 작성해주세요.
                                            </label>
                                            <div className="relative">
                                                <textarea
                                                    value={survey.feedback}
                                                    onChange={(e) => setSurvey(prev => ({ ...prev, feedback: e.target.value.slice(0, 1000) }))}
                                                    placeholder="의견을 남겨주세요."
                                                    maxLength={1000}
                                                    className="w-full h-32 p-4 rounded-[12px] border border-line focus:border-primary-focus focus:outline-none resize-none pb-10"
                                                />
                                                <div className="absolute bottom-3 right-4 text-12 text-disabled">
                                                    <span className={survey.feedback.length >= 1000 ? 'text-error font-bold' : ''}>
                                                        {survey.feedback.length}
                                                    </span>
                                                    /1000자
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <button
                                onClick={handleFinalSubmit}
                                disabled={survey.rating === 0 || !survey.feedback.trim() || isSubmitting}
                                className={`font-heading w-full py-4 rounded-[12px] text-18 font-bold transition-colors ${survey.rating > 0 && survey.feedback.trim() && !isSubmitting ? 'bg-primary text-white hover:bg-primary-hover' : 'bg-fill-strong text-disabled'
                                    }`}
                            >
                                {isSubmitting ? "제출 중..." : "제출하기"}
                            </button>
                            {(survey.rating === 0 || !survey.feedback.trim()) && (
                                <p className="text-center text-13 text-error mt-4">* 평점과 문항 2를 모두 입력해주세요.</p>
                            )}
                        </div>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="bg-page min-h-screen">
            <Head>
                <title>해봄 체험하기 (학생) | 해봄</title>
            </Head>
            <Header />
            <main className="max-w-[1000px] mx-auto py-20 px-6">
                <div className="bg-white rounded-[20px] p-10 shadow-sm">
                    <div className="mb-10 text-center">
                        <h1 className="text-32 font-bold mb-4 font-heading">해봄 체험하기</h1>
                        <p className="text-16 text-muted">간단한 진단을 통해 AI시대 나의 미래 역량 유형을 확인해보세요!</p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-12">
                        {/* 학생 정보 */}
                        <div className="space-y-4">
                            <h2 className="text-16 font-bold text-strong">
                                학생 정보
                                <span className="block mt-1 text-12 text-primary-400 font-normal">* 정확하게 작성해야 올바른 검사 결과가 나와요! ✨</span>
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label className="text-15 font-bold text-body">
                                        지역 <span className="text-error">*</span>
                                    </label>
                                    <select
                                        id="field-region"
                                        name="region"
                                        value={userInfo.region}
                                        onChange={handleInputChange}
                                        required
                                        className={`w-full px-4 py-3 rounded-[8px] border border-line focus:border-primary-focus focus:outline-none bg-page scroll-mt-24${ringCls("field-region")}`}
                                    >
                                        <option value="">선택해주세요</option>
                                        <option value="서울특별시">서울특별시</option>
                                        <option value="부산광역시">부산광역시</option>
                                        <option value="대구광역시">대구광역시</option>
                                        <option value="인천광역시">인천광역시</option>
                                        <option value="광주광역시">광주광역시</option>
                                        <option value="대전광역시">대전광역시</option>
                                        <option value="울산광역시">울산광역시</option>
                                        <option value="세종특별자치시">세종특별자치시</option>
                                        <option value="경기도">경기도</option>
                                        <option value="강원특별자치도">강원특별자치도</option>
                                        <option value="충청북도">충청북도</option>
                                        <option value="충청남도">충청남도</option>
                                        <option value="전북특별자치도">전북특별자치도</option>
                                        <option value="전라남도">전라남도</option>
                                        <option value="경상북도">경상북도</option>
                                        <option value="경상남도">경상남도</option>
                                        <option value="제주특별자치도">제주특별자치도</option>
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-15 font-bold text-body">
                                        성별 <span className="text-error">*</span>
                                    </label>
                                    <select
                                        id="field-gender"
                                        name="gender"
                                        value={userInfo.gender}
                                        onChange={handleInputChange}
                                        required
                                        className={`w-full px-4 py-3 rounded-[8px] border border-line focus:border-primary-focus focus:outline-none bg-page scroll-mt-24${ringCls("field-gender")}`}
                                    >
                                        <option value="">선택해주세요</option>
                                        <option value="MALE">남성</option>
                                        <option value="FEMALE">여성</option>
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-15 font-bold text-body">
                                        학교 <span className="text-error">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        id="field-school"
                                        name="school"
                                        value={userInfo.school}
                                        onChange={handleInputChange}
                                        placeholder="예: 코하초등학교"
                                        required
                                        maxLength={50}
                                        className={`w-full px-4 py-3 rounded-[8px] border border-line focus:border-primary-focus focus:outline-none bg-page scroll-mt-24${ringCls("field-school")}`}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-15 font-bold text-body">
                                        학년 <span className="text-error">*</span>
                                    </label>
                                    <select
                                        id="field-grade"
                                        name="grade"
                                        value={userInfo.grade}
                                        onChange={handleInputChange}
                                        required
                                        className={`w-full px-4 py-3 rounded-[8px] border border-line focus:border-primary-focus focus:outline-none bg-page scroll-mt-24${ringCls("field-grade")}`}
                                    >
                                        <option value="">선택해주세요</option>
                                        {GRADE_OPTIONS.map((g) => (
                                            <option key={g} value={g}>{g}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="space-y-2 md:col-span-2">
                                    <label className="text-15 font-bold text-body">
                                        닉네임 <span className="text-error">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        id="field-nickname"
                                        name="nickname"
                                        value={userInfo.nickname}
                                        onChange={handleInputChange}
                                        placeholder="실명 대신 별명을 적어주세요. 예: 해봄친구"
                                        required
                                        maxLength={20}
                                        className={`w-full px-4 py-3 rounded-[8px] border border-line focus:border-primary-focus focus:outline-none bg-page scroll-mt-24${ringCls("field-nickname")}`}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* 법정대리인 정보 */}
                        <div className="space-y-4 pb-10 border-b border-line-subtle">
                            <h2 className="text-16 font-bold text-strong">
                                법정대리인 정보 [선택]
                                <span className="block mt-1 text-12 text-primary-400 font-normal">* 작성해주신 연락처로 향후 이벤트 및 마케팅 관련 안내가 전송될 수 있습니다.</span>
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label className="text-15 font-bold text-body">이름</label>
                                    <input
                                        type="text"
                                        name="guardianName"
                                        value={userInfo.guardianName}
                                        onChange={handleInputChange}
                                        placeholder="예: 홍길동"
                                        maxLength={20}
                                        className="w-full px-4 py-3 rounded-[8px] border border-line focus:border-primary-focus focus:outline-none bg-page"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-15 font-bold text-body">전화번호</label>
                                    <input
                                        type="text"
                                        id="field-guardianPhone"
                                        name="guardianPhone"
                                        value={formatPhoneAsYouType(userInfo.guardianPhone)}
                                        onChange={handleInputChange}
                                        placeholder="010-1234-5678"
                                        maxLength={13}
                                        className={`w-full px-4 py-3 rounded-[8px] border focus:outline-none bg-page transition-colors scroll-mt-24${ringCls("field-guardianPhone")} ${userInfo.guardianPhone && !/^\d{11}$/.test(userInfo.guardianPhone)
                                            ? 'border-error-line focus:border-error'
                                            : 'border-line focus:border-primary-focus'
                                            }`}
                                    />
                                    {userInfo.guardianPhone && !/^\d{11}$/.test(userInfo.guardianPhone) && (
                                        <p className="text-12 text-error">
                                            * 전화번호 11자리를 정확하게 입력해주세요.
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* 문항 섹션 */}
                        <div className="space-y-10">
                            {EXHIBITION_QUESTIONS.map((q, idx) => (
                                <div
                                    key={q.id}
                                    id={`field-q-${q.id}`}
                                    className={`space-y-5 scroll-mt-24 rounded-[16px]${
                                        highlightId === `field-q-${q.id}`
                                            ? " ring-2 ring-error-line"
                                            : ""
                                    }`}
                                >
                                    <div className="flex justify-between items-end gap-2">
                                        <div className="space-y-2">
                                            <p className="text-14 font-bold text-primary mb-1">Q{idx + 1}</p>
                                            <h3 className="text-20 font-bold leading-tight text-strong whitespace-pre-wrap font-heading">
                                                {q.id === 13 ? (
                                                    <>
                                                        {q.content}
                                                        <span className="block mt-2 text-14 text-primary-400 font-normal">
                                                            * 중요하다고 생각하는 순서대로 번호를 눌러주세요.
                                                        </span>
                                                    </>
                                                ) : q.content}
                                            </h3>
                                        </div>
                                        {q.id === 13 && (
                                            <button
                                                type="button"
                                                onClick={handleResetRanking}
                                                disabled={ranking.length === 0}
                                                className={`px-4 py-2 text-14 font-bold rounded-[8px] transition-all mb-2 ${ranking.length > 0
                                                    ? 'bg-primary text-white hover:bg-primary-hover shadow-sm'
                                                    : 'bg-fill text-disabled cursor-not-allowed'
                                                    }`}
                                            >
                                                RESET
                                            </button>
                                        )}
                                    </div>

                                    <div className={`grid gap-3 ${q.id === 13 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
                                        {q.options.map((option: any) => {
                                            const selected = isOptionSelected(q.id, option.id);
                                            const rankNum = q.id === 13 ? getRankingNumber(option.id) : null;

                                            return (
                                                <label
                                                    key={option.id}
                                                    onClick={(e) => {
                                                        if (q.id === 13) {
                                                            e.preventDefault();
                                                            handleOptionChange(q.id, option.id);
                                                        }
                                                    }}
                                                    className={`flex items-center gap-4 px-6 py-4 rounded-[16px] border-2 cursor-pointer transition-all ${selected
                                                        ? 'bg-primary-soft border-primary shadow-md'
                                                        : 'bg-white border-line-subtle hover:border-line-strong'
                                                        }`}
                                                >
                                                    <input
                                                        type="radio"
                                                        name={`q-${q.id}`}
                                                        value={option.id}
                                                        checked={selected}
                                                        onChange={() => handleOptionChange(q.id, option.id)}
                                                        className="hidden"
                                                    />
                                                    <div className={`w-9 h-9 rounded-full border-2 flex-shrink-0 flex items-center justify-center text-16 font-black ${selected ? 'bg-primary border-primary text-white' : 'border-line text-gray-300'
                                                        }`}>
                                                        {q.id === 13 ? (rankNum || "-") : (q.options.indexOf(option) + 1)}
                                                    </div>
                                                    <span className={`text-17 leading-relaxed flex-1 whitespace-pre-wrap ${selected ? 'text-primary-ink font-bold' : 'text-body'}`}>
                                                        {option.text}
                                                    </span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="pt-10">
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className={`font-heading w-full py-5 rounded-[16px] text-20 font-bold transition-all bg-primary text-white shadow-lg ${isSubmitting
                                    ? 'opacity-60 cursor-wait'
                                    : 'hover:bg-primary-hover transform active:scale-[0.98] cursor-pointer'
                                    }`}
                            >
                                {isSubmitting ? "제출 중..." : "제출하고 결과 확인하기"}
                            </button>
                            {!isFormValid() && (
                                <p className="text-center text-13 text-error mt-4">
                                    * 모든 필수 정보를 입력하고 문제를 풀어주세요.
                                </p>
                            )}
                        </div>
                    </form>
                </div>
            </main>
        </div>
    );
};

export default Play;
